import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Command } from "cmdk"
import { cn } from "@/lib/utils"
import {
  Home,
  Coins,
  Wallet,
  CardSim,
  Smartphone,
  ShoppingBag,
  User,
  Users,
  FileText,
  Megaphone,
  LogOut,
  Zap,
} from "lucide-react"
import { useAuthStore, useBrandStore } from "@/stores"
import * as Dialog from "@radix-ui/react-dialog"

// Orden alineado con Sidebar; cada ítem tiene permiso para filtrar por rol (Admin solo ve Inicio, Usuarios, SIMs, Perfil)
function tienePermiso(permisos: Record<string, string> | null | undefined, nombrePermiso: string): boolean {
  if (!permisos || typeof permisos !== "object" || Object.keys(permisos).length === 0) return true
  const valor = permisos[nombrePermiso]
  // Consistent with Sidebar: missing key = no permission (restrictive)
  if (valor === undefined || valor === null) return false
  return ["e", "sl", "u"].includes(String(valor))
}

// Espejo del array de Sidebar.tsx — mantener sincronizados.
const COMMANDS: Array<{
  id: string; label: string; href: string;
  icon: typeof Home; permiso: string; keywords: string[];
  soloTipos?: string[];
}> = [
  { id: "inicio", label: "Inicio", href: "/index", icon: Home, permiso: "home_all", keywords: ["inicio", "dashboard", "home"] },
  { id: "saldos", label: "Saldos", href: "/saldos", icon: Wallet, permiso: "saldos_all", keywords: ["saldos", "wallets", "tiempo aire"], soloTipos: ["A","M","B","C","R"] },
  { id: "ventas", label: "Ventas", href: "/ventas", icon: Coins, permiso: "ventas_all", keywords: ["ventas", "reporte"], soloTipos: ["Admin","A","B","C","R"] },
  { id: "sims", label: "Inventario de SIMs", href: "/inventario-de-sims", icon: CardSim, permiso: "inventarios_all", keywords: ["sims", "inventario"], soloTipos: ["Admin","A","B","C"] },
  { id: "recarga", label: "Recarga", href: "/recarga", icon: Zap, permiso: "activacion_all", keywords: ["recarga", "recargar", "tiempo aire", "saldo"], soloTipos: ["B","C","R"] },
  { id: "portabilidad", label: "Portabilidad y Activación", href: "/portabilidad-activacion", icon: Smartphone, permiso: "activacion_all", keywords: ["portabilidad", "activación", "portar", "activar", "cambio región", "nir"], soloTipos: ["B","C"] },
  { id: "compras", label: "Compras", href: "/compras", icon: ShoppingBag, permiso: "compras_all", keywords: ["compras", "depósitos"], soloTipos: ["A","B","C","R"] },
  { id: "perfil", label: "Perfil", href: "/perfil", icon: User, permiso: "perfil_all", keywords: ["perfil", "cuenta"] },
  { id: "usuarios", label: "Usuarios", href: "/usuarios", icon: Users, permiso: "usuarios_all", keywords: ["usuarios"], soloTipos: ["Admin","A","B"] },
  { id: "reportes", label: "Reportes", href: "/reportes", icon: FileText, permiso: "reportes_all", keywords: ["reportes"], soloTipos: ["Admin","A","M","B","C"] },
  { id: "publicidad", label: "Publicidad y manuales", href: "/publicidad-y-manuales", icon: Megaphone, permiso: "publicidad_y_anuncios_all", keywords: ["publicidad", "manuales"] },
]

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { config } = useBrandStore()
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  const handleSelect = (href: string) => {
    if (href === "/logout") {
      logout()
      onOpenChange(false)
      return
    }
    navigate(href)
    onOpenChange(false)
  }

  if (!mounted) return null

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-xl border bg-popover p-0 shadow-soft-lg text-popover-foreground"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
        <Command
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-2.5"
          loop
        >
          <div className="flex items-center border-b px-3">
            <span className="text-muted-foreground mr-2">⌘K</span>
            <Command.Input
              placeholder="Buscar sección..."
              className="flex h-12 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <Command.List className="max-h-[300px] overflow-y-auto py-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No hay resultados.
            </Command.Empty>
            <Command.Group heading="Navegación">
              {COMMANDS.filter((item) => {
                if (!tienePermiso(user?.permisos as Record<string, string>, item.permiso)) return false
                if (item.soloTipos && !item.soloTipos.includes(user?.tipo_usuario ?? "")) return false
                return true
              }).map((item) => {
                const Icon = item.icon
                return (
                  <Command.Item
                    key={item.id}
                    value={`${item.label} ${item.keywords.join(" ")}`}
                    onSelect={() => handleSelect(item.href)}
                    className={cn(
                      "relative flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm outline-none transition-colors",
                      "aria-selected:bg-accent aria-selected:text-accent-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    {item.label}
                  </Command.Item>
                )
              })}
            </Command.Group>
            <Command.Separator />
            <Command.Group heading="Sesión">
              <Command.Item
                value="cerrar sesión logout"
                onSelect={() => handleSelect("/logout")}
                className={cn(
                  "relative flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm outline-none transition-colors",
                  "aria-selected:bg-destructive/10 aria-selected:text-destructive"
                )}
              >
                <LogOut className="h-4 w-4 shrink-0" />
                Cerrar sesión
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
        <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>{config.name}</span>
          <span>{user?.nombre} {user?.apellidos}</span>
        </div>
      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
